/* Copyright (c) 2020-2024 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo, useState } from "react";
import { Row, Col } from "react-awesome-styled-grid";
import { Box, Button, ModalConfirm, Text } from "@nordcloud/gnui";
import { useExternalIntegrationsQuery } from "~/generated/graphql";
import {
  BreadcrumbsBox,
  LoaderWrap,
  ReactTable,
  TableLoader,
  UniversalWrap,
  UnstyledLink,
} from "~/components";
import { useDisclosure } from "~/hooks";
import { ROUTES } from "~/routing/routes";
import { AddSandbox } from "~/views/externalIntegrations/components/AddSandbox";
import { ExternalIntegrationsColumns } from "./ExternalIntegrationsColumns";
import { useRemoveExternalIntegration } from "./hooks/useRemoveExternalInegration";

const breadcrumbLabels = [
  {
    value: "external-integrations",
    label: "External Integrations",
  },
];

export function ExternalIntegrationsPage() {
  const [integrationClicked, setIntegrationClicked] = useState<
    | {
        name: string;
        id: string;
      }
    | undefined
  >();

  const { data, error, loading } = useExternalIntegrationsQuery();
  const [removeIntegration, { loading: isRemoveLoading }] =
    useRemoveExternalIntegration(integrationClicked?.id);

  const { isOpen, open, close } = useDisclosure();

  const handleIntegrationDeleteClick = (id: string, name: string) => {
    setIntegrationClicked({ name, id });
    open();
  };

  const columns = useMemo(
    () =>
      ExternalIntegrationsColumns({ onDelete: handleIntegrationDeleteClick }),
    []
  );

  return (
    <LoaderWrap loading={isRemoveLoading}>
      <Row>
        <Col>
          <BreadcrumbsBox
            title="External Integrations"
            labels={breadcrumbLabels}
          >
            <AddSandbox />
            <UnstyledLink to={ROUTES.externalIntegrations.create}>
              <Button icon="plus">Add New Integration</Button>
            </UnstyledLink>
          </BreadcrumbsBox>
        </Col>
      </Row>
      <Row>
        <Col>
          <Box spacing="spacing04">
            <UniversalWrap
              errorProps={{ error }}
              loaderProps={{ loading, Component: Loader }}
            >
              <ReactTable
                striped
                columns={columns}
                sort={false}
                data={data?.externalIntegrations?.integrations ?? []}
              />
            </UniversalWrap>
          </Box>
        </Col>
      </Row>
      <ModalConfirm
        isOpen={isOpen}
        confirm={() => removeIntegration(close)}
        actionLabel="Delete"
        contentLabel="Delete External Integration"
        onClose={close}
      >
        <Text>
          Are you sure you want to delete &quot;{integrationClicked?.name}
          &quot;?
        </Text>
      </ModalConfirm>
    </LoaderWrap>
  );
}

function Loader() {
  return (
    <TableLoader
      gapY={10}
      gapX={6}
      rows={[
        {
          count: 8,
          height: 60,
          items: [
            {
              widthPercent: 50,
            },
            {
              widthPercent: 25,
            },
            {
              widthPercent: 25,
            },
          ],
        },
      ]}
    />
  );
}
